/**
 * How an invoice identifies itself to a HUMAN.
 *
 * There are two identifiers on an invoice and they must never be confused:
 *
 *   invoiceNumber          the DOC ID — `{auctionId}_{uid}`. A primary key, not a
 *                          label. Every route resolves it (doc(db,'invoices', n)),
 *                          every link is built from it, and it is
 *                          settle-invoice.mjs's --invoice= argument. Never change
 *                          it, and never put it in front of a buyer as "your
 *                          invoice number".
 *
 *   displayInvoiceNumber   the human one — 8001, 8002, … Assigned once, from an
 *                          atomic counter, recorded in invoiceNumberLedger, and
 *                          never reassigned. Continues AuctionFlex (last: 7445).
 *                          See backfill-invoice-numbers.mjs.
 *
 * Every buyer-facing surface routes through here so the three of them (the
 * printed invoice, the bidder's list, the bidder's detail page) cannot drift —
 * the same lesson as lotNumberLabel() in utils/lotDisplay.ts.
 *
 * FALLBACK: an invoice that has not been numbered yet still has to render
 * something rather than "INVOICE #undefined", so it falls back to the doc id.
 * That is deliberately ugly — an ugly identifier on screen is a visible prompt
 * to run the backfill, whereas a blank space is not.
 */

/** Minimal shape; every page's local Invoice interface satisfies it. */
export interface DisplayableInvoice {
  invoiceNumber?: string;
  displayInvoiceNumber?: number;
}

/** True once a human number has been assigned. */
export const hasDisplayNumber = (inv: DisplayableInvoice | null | undefined): boolean =>
  typeof inv?.displayInvoiceNumber === 'number' && Number.isFinite(inv.displayInvoiceNumber);

/** Compact form for a table cell: "#8001", or the doc id if unnumbered. */
export const invoiceNumberShort = (inv: DisplayableInvoice | null | undefined): string =>
  hasDisplayNumber(inv) ? `#${inv!.displayInvoiceNumber}` : (inv?.invoiceNumber || '—');

/** Full form for a document header: "INVOICE #8001", or the doc id if unnumbered. */
export const invoiceNumberLabel = (inv: DisplayableInvoice | null | undefined): string =>
  hasDisplayNumber(inv) ? `INVOICE #${inv!.displayInvoiceNumber}` : (inv?.invoiceNumber || '—');
